import React from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Loader2, HelpCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { MODULE_HELP } from "../../lib/tourSteps";

// Animated count-up number (transform/opacity only, 60fps)
export function CountUp({ value, format, className = "" }) {
  const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const spring = useSpring(reduce ? value : 0, { stiffness: 90, damping: 20 });
  const display = useTransform(spring, (v) => (format ? format(v) : Math.round(v).toString()));
  React.useEffect(() => { spring.set(value); }, [value, spring]);
  if (reduce) return <span className={className}>{format ? format(value) : Math.round(value)}</span>;
  return <motion.span className={className}>{display}</motion.span>;
}

export function HelpPopover() {
  const { language } = useApp();
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const help = MODULE_HELP[pathname];
  if (!help) return null;
  const L = language === "हिंदी" ? "hi" : "en";
  return (
    <div className="relative inline-flex">
      <button onClick={() => setOpen((v) => !v)} data-testid="module-help-btn" aria-label="Help"
        className="h-8 w-8 rounded-full border border-black/10 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent tap">
        <HelpCircle className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-[61] w-80 max-w-[calc(100vw-32px)] rounded-2xl bg-[rgba(255,255,255,0.97)] backdrop-blur-[8px] border border-black/5 shadow-2xl p-4 text-[#1a1a1a]" data-testid="module-help-popover">
            <div className="flex items-start gap-2.5">
              <span className="h-9 w-9 rounded-xl bg-green-100 flex items-center justify-center text-lg shrink-0">{help.emoji}</span>
              <div className="flex-1">
                <p className="font-bold text-sm mb-1 leading-snug">{help[L].t}</p>
                <p className="text-sm text-black/65 leading-relaxed">{help[L].x}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-black/40 hover:text-black/70"><X className="h-4 w-4" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6" data-testid="page-header">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
            <HelpPopover />
          </div>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "primary", testId }) {
  const tones = {
    primary: "text-primary bg-accent",
    sky: "text-sky bg-sky/10",
    earth: "text-earth bg-earth/10",
    warning: "text-warning bg-warning/10",
    success: "text-success bg-success/10",
  };
  return (
    <div className="rounded-2xl border bg-card p-5 shadow-sm card-lift" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon && (
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tones[tone]}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <p className="mt-3 text-2xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4" data-testid="empty-state">
      {Icon && (
        <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center mb-4">
          <Icon className="h-7 w-7 text-primary" />
        </div>
      )}
      <h3 className="font-semibold text-lg">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loader({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="loader">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
      <p className="text-sm mt-3">{label}</p>
    </div>
  );
}

export function PageWrap({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {children}
    </motion.div>
  );
}

export function SoftCard({ children, className = "", testId }) {
  return (
    <div className={`rounded-2xl border bg-card p-6 shadow-sm min-w-0 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

// staggered entrance helpers for grids/lists (iOS-like)
export const staggerContainer = { animate: { transition: { staggerChildren: 0.06 } } };
export const staggerItem = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] } },
};

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-xl bg-muted/70 ${className}`} data-testid="skeleton" />;
}

// Small "source · timestamp" footer for every data widget
export function SourceTag({ source, time, className = "" }) {
  if (!source && !time) return null;
  return (
    <p className={`text-[10px] text-muted-foreground mt-2 ${className}`} data-testid="source-tag">
      {source}{source && time ? " · " : ""}{time}
    </p>
  );
}
